import {
	NIBBLE_HEIGHT,
	NIBBLE_RATE,
	NIBBLE_WIDTH,
	type NibbleInput,
	type NibbleState,
	nibbleHeading,
	nibbleTurnToward,
	nibbleX,
	nibbleY,
} from "@playpal/core";
import { useEffect, useRef } from "react";
import { type SwipeDirection, useGestures } from "../lib/useGestures";

const CELL = 100 / NIBBLE_WIDTH;
const STEP_MS = 1000 / NIBBLE_RATE;

/** The world's width over its height — what the stage scales to fit. */
export const NIBBLE_ASPECT = NIBBLE_WIDTH / NIBBLE_HEIGHT;

/** Where a cell sits, as a share of the board. */
function place(at: number, scale = 1) {
	return {
		width: `${CELL}%`,
		height: `${100 / NIBBLE_HEIGHT}%`,
		// Percentages on `translate` are of the element, so this lands the square
		// on its own cell — and a transform animates without laying the page out
		// again, which matters eight times a second. The scale rides along for
		// the snake's taper; it costs nothing extra.
		transform:
			scale === 1
				? `translate(${nibbleX(at) * 100}%, ${nibbleY(at) * 100}%)`
				: `translate(${nibbleX(at) * 100}%, ${nibbleY(at) * 100}%) scale(${scale})`,
		transitionDuration: `${STEP_MS}ms`,
	};
}

/**
 * A segment's colour, brightest at the head and shading down the tail, so the
 * snake reads as one creature rather than a queue of squares.
 */
function segmentColour(index: number, length: number): string {
	const t = length <= 1 ? 0 : index / (length - 1);
	return `hsl(${95 + t * 55} 68% ${60 - t * 22}%)`;
}

/** How far to turn the face so the eyes look the way the snake is going. */
const FACE_ROTATION = [0, -90, 180, 90] as const;

/** The very subtle checkerboard under everything, drawn as one background. */
const CHECKER = {
	backgroundImage: "repeating-conic-gradient(rgb(255 255 255 / 0.04) 0% 25%, transparent 0% 50%)",
	backgroundSize: `${CELL * 2}% ${(100 / NIBBLE_HEIGHT) * 2}%`,
};

interface FieldProps {
	state: NibbleState;
	onPress: (input: NibbleInput) => void;
	/** Turns taken but not yet applied, so a swipe knows where it is going. */
	pending: () => readonly NibbleInput[];
	live: boolean;
}

/**
 * The board itself, sized by whatever it is put inside — and the thing you
 * swipe.
 *
 * Swiping is how a snake has been played on a phone for fifteen years, and it
 * is what a child tries first on a full-screen board. It went out with two
 * buttons and a keyboard, which is how this is played on a laptop and nowhere
 * else. The buttons stay: they are discoverable, they are what a screen reader
 * can find, and some children prefer them.
 *
 * The board is hidden from a screen reader on purpose, and not as a shortcut:
 * this is a square moving eight times a second, and any label worth reading is
 * out of date before it has been read. The score above it is a live region, and
 * that is the part there is something to say about.
 */
export function NibbleField({ state, onPress, pending, live }: FieldProps) {
	const board = useRef<HTMLDivElement>(null);

	useGestures(board, {
		enabled: live,
		onSwipe: (direction) => {
			const turn = turnFor(state.direction, pending(), direction);
			if (turn) onPress(turn);
		},
	});

	// The last apple eaten, remembered so the crunch can flash where it was.
	// A ref rather than state: this is derived from the props on every render
	// and must never itself cause one.
	const crunch = useRef({ at: -1, count: 0 });
	if (state.eaten !== crunch.current.count) {
		crunch.current = { at: state.body[0], count: state.eaten };
	}

	const length = state.body.length;

	return (
		<div
			ref={board}
			aria-hidden="true"
			style={{ aspectRatio: `${NIBBLE_WIDTH} / ${NIBBLE_HEIGHT}`, ...CHECKER }}
			className="relative touch-none select-none overflow-hidden rounded-3xl bg-emerald-950/90 shadow-inner ring-4 ring-emerald-900/40"
		>
			{/* The apple: shiny, leafy, and gently asking to be eaten. */}
			<div className="absolute transition-transform ease-linear" style={place(state.apple)}>
				<div className="absolute inset-[10%] animate-soft-pulse">
					<div
						className="absolute inset-x-[4%] top-[16%] bottom-0 rounded-[46%]"
						style={{
							background:
								"radial-gradient(circle at 32% 30%, #fecaca 0%, #ef4444 45%, #991b1b 100%)",
						}}
					/>
					<span className="absolute left-1/2 top-0 h-[24%] w-[10%] -translate-x-1/2 rounded-full bg-amber-800" />
					<span
						className="absolute left-[52%] top-[2%] h-[20%] w-[36%] bg-green-500"
						style={{ borderRadius: "75% 0 75% 0", transform: "rotate(-20deg)" }}
					/>
				</div>
			</div>

			{state.body.map((at, index) => {
				const head = index === 0;
				return (
					<div
						// The snake is a queue, so a cell is not a stable identity — but
						// its place in the body is. Keying on that makes each segment
						// slide one cell down the tail, which is what slithering is.
						key={index}
						className={`absolute transition-transform ease-linear ${
							head
								? "z-10 rounded-[42%] ring-2 ring-lime-100"
								: index % 2 === 0
									? "rounded-[30%]"
									: "rounded-[36%]"
						}`}
						style={{
							...place(at, head ? 1 : 1 - Math.min(0.3, (index / length) * 0.3)),
							backgroundColor: segmentColour(index, length),
						}}
					>
						{head && (
							// Turned to face the way the snake is travelling, popped when
							// it eats, forever slightly wiggling: three wrappers because
							// each is its own transform.
							<div
								className="absolute inset-0"
								style={{ transform: `rotate(${FACE_ROTATION[state.direction] ?? 0}deg)` }}
							>
								<div
									key={state.eaten}
									className={state.eaten > 0 ? "absolute inset-0 animate-pop" : "absolute inset-0"}
								>
									<div className="absolute inset-0 animate-wiggle">
										<span className="absolute top-[10%] right-[6%] h-[32%] w-[32%] rounded-full bg-white">
											<span className="absolute top-1/2 right-[12%] h-[55%] w-[55%] -translate-y-1/2 rounded-full bg-slate-900" />
										</span>
										<span className="absolute bottom-[10%] right-[6%] h-[32%] w-[32%] rounded-full bg-white">
											<span className="absolute top-1/2 right-[12%] h-[55%] w-[55%] -translate-y-1/2 rounded-full bg-slate-900" />
										</span>
									</div>
								</div>
							</div>
						)}
					</div>
				);
			})}

			{/* The crunch: a burst where the apple just was, gone in a blink. */}
			{crunch.current.count > 0 && (
				<div
					key={crunch.current.count}
					className="pointer-events-none absolute z-20 flex items-center justify-center"
					style={place(crunch.current.at)}
				>
					<span className="animate-crunch text-[min(4vw,1.6rem)] leading-none">✨</span>
				</div>
			)}

			{state.over && <div className="absolute inset-0 bg-rose-900/40" />}
		</div>
	);
}

/**
 * The turn that heads `direction`, given where the snake will be pointing once
 * everything already queued has been walked.
 *
 * Both the keys and a swipe name an absolute direction, and both have to be
 * read against the queue rather than the picture: heading east, an up and a
 * left inside the same eighth of a second are a left turn and then another
 * left turn, not a left turn and then nothing.
 */
function turnFor(
	heading: number,
	queued: readonly NibbleInput[],
	direction: SwipeDirection,
): NibbleInput | null {
	const target = SWIPE_HEADINGS[direction];
	const facing = queued.reduce(
		(current, turn) => (current + (turn.turn === "left" ? 1 : 3)) % 4,
		heading,
	);
	return nibbleTurnToward(facing, nibbleHeading(target.x, target.y));
}

const SWIPE_HEADINGS: Record<SwipeDirection, { x: number; y: number }> = {
	right: { x: 1, y: 0 },
	up: { x: 0, y: -1 },
	left: { x: -1, y: 0 },
	down: { x: 0, y: 1 },
};

interface ControlProps {
	state: NibbleState;
	onPress: (input: NibbleInput) => void;
	/** Turns taken but not yet applied, so a second key knows where it is going. */
	pending: () => readonly NibbleInput[];
	/** False before the run starts and after it ends — keys stop working then. */
	live: boolean;
}

/**
 * Left and right rather than up and down.
 *
 * A snake turns relative to where it is pointing, so two buttons say everything
 * four could, and on a phone two big ones are far easier to hit than a four-way
 * pad.
 */
export function NibbleControls({ state, onPress, pending, live }: ControlProps) {
	useArrowKeys(state.direction, onPress, pending, live);

	return (
		<div className="py-2">
			<div className="flex justify-center gap-4">
				<TurnButton label="Turn left" glyph="⬅️" onPress={() => onPress({ turn: "left" })} />
				<TurnButton label="Turn right" glyph="➡️" onPress={() => onPress({ turn: "right" })} />
			</div>
			<p className="mt-2 text-center text-sm font-semibold opacity-60">
				Or swipe the board the way you want to go
			</p>
		</div>
	);
}

function TurnButton({
	label,
	glyph,
	onPress,
}: {
	label: string;
	glyph: string;
	onPress: () => void;
}) {
	return (
		<button
			type="button"
			aria-label={label}
			// Pointer-down, not click: at eight steps a second the wait for a click
			// to be confirmed is most of a step, and it reads as the game ignoring
			// you. `touch-none` keeps a fast double-tap from zooming the page.
			onPointerDown={(event) => {
				event.preventDefault();
				onPress();
			}}
			className="flex h-20 w-36 touch-none select-none items-center justify-center rounded-3xl bg-white/15 text-4xl shadow-lg transition active:scale-95"
		>
			<span aria-hidden="true">{glyph}</span>
		</button>
	);
}

/**
 * Arrow keys, for whoever is playing on a laptop.
 *
 * They name a direction and the game only has left and right, so pressing the
 * way you are already going does nothing, and so does pressing the way you came
 * — a snake that could turn back would bite its own neck on the next step.
 *
 * The direction they are measured against is the one after the turns already
 * queued, not the one on screen. Measuring against the picture silently threw
 * away the second of two quick presses: heading east, Up then Left inside the
 * same eighth of a second gave a left turn and then nothing, because Left is
 * not a turn from east — but it is a perfectly good left turn from the north
 * the queue was about to produce. Rounding a corner with two fast presses is
 * the commonest thing anybody does in this game, and it was the one thing the
 * keyboard could not do. The on-screen buttons were never affected: they say
 * left and right, which need no direction to interpret.
 */
function useArrowKeys(
	heading: number,
	onPress: (input: NibbleInput) => void,
	pending: () => readonly NibbleInput[],
	live: boolean,
) {
	useEffect(() => {
		if (!live) return;

		const onKeyDown = (event: KeyboardEvent) => {
			const direction = ARROW_DIRECTIONS[event.key];
			if (!direction) return;
			// The page does not scroll during a run, but the browser will try.
			event.preventDefault();
			const turn = turnFor(heading, pending(), direction);
			if (turn) onPress(turn);
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [heading, onPress, pending, live]);
}

const ARROW_DIRECTIONS: Record<string, SwipeDirection | undefined> = {
	ArrowRight: "right",
	ArrowUp: "up",
	ArrowLeft: "left",
	ArrowDown: "down",
};
